/**
 * BranchOverrideModal — set / replace / clear today's branch override
 * (CONFIG-03). Opens from `ActiveBranchChip` (TopBar) and from the
 * "Change branch for today" link on `CartBootstrapStep`.
 *
 * Surface (per design §5.3):
 *   Header     : "Work from a different branch today"
 *   Copy       : "Your home branch is {primaryBranchName}. Pick a different
 *                 branch to use for today only. Your home branch returns
 *                 automatically tomorrow."
 *   Branch select : sourced from config.branches, IsActive only
 *   Reason input  : optional free-text (audit hint)
 *   CTAs       : Cancel (ghost) · Set for today (primary)
 *                + Reset to primary (tertiary, only when override active)
 *
 * On success: refreshBranch() → close modal → emit `branch.override.set`
 * (or `.cleared`) SessionEvent.
 *
 * TODO: design §5.3 also mentions `visibility.selectableBranches` region
 * scoping (Manager/Supervisor see only their region; Executives/Admins see
 * all). v3 v1 ships unscoped — every active branch is selectable. Wire
 * region-scope filtering once `Region.branchCodes` is plumbed through
 * the auth context.
 */
import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { ModalShell, Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useEmitEvent } from '@/modules/RAO/hooks/useEmitEvent';

export interface BranchOverrideModalProps {
  open: boolean;
  onClose: () => void;
}

export function BranchOverrideModal({ open, onClose }: BranchOverrideModalProps): JSX.Element {
  const { user, refreshBranch } = useAuth();
  const client = useApiClient();
  const { config } = useConfig();
  const emitEvent = useEmitEvent();

  const overrideActive = user?.overrideActive === true;
  const primaryCode = user?.primaryBranchCode ?? user?.branchCode ?? '';
  const primaryName = user?.primaryBranchName ?? '';
  const activeCode = user?.activeBranchCode ?? user?.branchCode ?? '';

  const [selected, setSelected] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state every time the modal opens — default the dropdown
  // to the currently-active branch so submit-without-changing is a no-op.
  useEffect(() => {
    if (open) {
      setSelected(activeCode);
      setReason('');
      setError(null);
      setSubmitting(false);
      setResetting(false);
    }
  }, [open, activeCode]);

  const branches = useMemo(() => {
    return [...(config.branches ?? [])]
      .filter((b) => b.isActive !== false)
      .sort((a, b) =>
        String(a.branchCode ?? '').localeCompare(String(b.branchCode ?? '')),
      );
  }, [config.branches]);

  const handleSubmit = async (evt: FormEvent<HTMLFormElement>): Promise<void> => {
    evt.preventDefault();
    if (!selected || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await client.retail.session.setBranchOverride({
        branchCode: selected,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      if (!res.success) {
        setError(res.error ?? 'Could not set branch override.');
        return;
      }
      emitEvent({
        eventType: 'branch.override.set',
        eventCategory: 'session',
        severity: 'info',
        payload: {
          branchCode: selected,
          previousActive: activeCode,
          primary: primaryCode,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      });
      // 2026-10-16 (MacKenzie): never close as if it worked when the app couldn't load the new branch.
      if (!(await refreshBranch())) {
        setError('Your branch was saved, but the app could not load it. Close and reopen the Retail app, then check the branch shown at the top.');
        return;
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set branch override.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    if (resetting) return;
    setResetting(true);
    setError(null);
    try {
      const res = await client.retail.session.clearBranchOverride();
      if (!res.success) {
        setError(res.error ?? 'Could not reset to primary branch.');
        return;
      }
      emitEvent({
        eventType: 'branch.override.cleared',
        eventCategory: 'session',
        severity: 'info',
        payload: { previousActive: activeCode, primary: primaryCode },
      });
      await refreshBranch();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reset to primary branch.');
    } finally {
      setResetting(false);
    }
  };

  const homeBranchLabel = primaryName
    ? `${primaryCode} — ${primaryName}`
    : primaryCode || 'your home branch';

  const footer = (
    <>
      {overrideActive && (
        <Button
          type="button"
          variant="ghost"
          onClick={handleReset}
          disabled={submitting || resetting}
          style={{ marginRight: 'auto' }}
        >
          {resetting ? 'Resetting…' : 'Reset to primary'}
        </Button>
      )}
      <Button type="button" variant="ghost" onClick={onClose} disabled={submitting || resetting}>
        Cancel
      </Button>
      <Button
        type="submit"
        variant="primary"
        form="branch-override-form"
        disabled={
          submitting ||
          resetting ||
          !selected ||
          (selected === activeCode && reason.trim().length === 0)
        }
      >
        {submitting ? 'Saving…' : 'Set for today'}
      </Button>
    </>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Work from a different branch today"
      size="sm"
      footer={footer}
    >
      <p style={{ margin: 0, fontSize: 'var(--fs-body)', color: 'var(--ink-700)', lineHeight: 'var(--lh-body)' }}>
        Your home branch is <strong>{homeBranchLabel}</strong>. Pick a different
        branch to use for today only — your home branch returns automatically
        tomorrow.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            padding: 'var(--sp-5) var(--sp-6)',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-body)',
          }}
        >
          {error}
        </div>
      )}

      <form id="branch-override-form" onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label
            htmlFor="branch-override-select"
            style={{ fontSize: 'var(--fs-caption)', fontWeight: 500, color: 'var(--ink-800)' }}
          >
            Branch for today
          </label>
          <select
            id="branch-override-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            required
            style={{
              padding: 'var(--sp-5) var(--sp-6)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--r-md)',
              fontSize: 'var(--fs-body)',
              fontFamily: 'inherit',
              background: 'var(--surface-card)',
              color: 'var(--ink-900)',
            }}
          >
            <option value="" disabled>
              Select a branch…
            </option>
            {branches.map((b) => (
              <option key={b.branchCode} value={b.branchCode}>
                {b.branchName ? `${b.branchCode} — ${b.branchName}` : b.branchCode}
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
            marginTop: 'var(--sp-6)',
          }}
        >
          <label
            htmlFor="branch-override-reason"
            style={{ fontSize: 'var(--fs-caption)', fontWeight: 500, color: 'var(--ink-800)' }}
          >
            Reason (optional)
          </label>
          <input
            id="branch-override-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Covering for JDOE"
            maxLength={256}
            style={{
              padding: 'var(--sp-5) var(--sp-6)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--r-md)',
              fontSize: 'var(--fs-body)',
              fontFamily: 'inherit',
              background: 'var(--surface-card)',
              color: 'var(--ink-900)',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>
            Helps with audit. Visible to your supervisor and compliance.
          </span>
        </div>
      </form>
    </ModalShell>
  );
}
